import { config } from '@iheartjane/config';
import { Mfa, getMfaContactDisplay, useMfa } from '@iheartjane/menu';
import { Box } from '@iheartjane/reefer';
import type { AerosyncWidget } from 'aerosync-web-sdk';
import { useCallback, useEffect, useState } from 'react';
import type { MutableRefObject } from 'react';

import { useJaneUser } from '@jane/shared/data-access';

import { AeropayLoading } from './aeropayLoading';

export interface AeropayScreenProps {
  afterVerifySuccess?: () => void;
  isLinkingBankAccount: boolean;
  isLoadingWidget: boolean;
  onCancel?: () => void;
  showCancelButton?: boolean;
  widgetRef: MutableRefObject<AerosyncWidget | null>;
}

export const WIDGET_CONTAINER_ID = 'aerosync-widget';

export const AeropayScreen = ({
  afterVerifySuccess,
  isLinkingBankAccount,
  isLoadingWidget,
  onCancel,
  showCancelButton = true,
  widgetRef,
}: AeropayScreenProps) => {
  const [userVerified, setUserVerified] = useState(false);

  const { data: userData } = useJaneUser();
  const {
    canResendCode,
    canSendCode,
    codeResent,
    codeSent,
    errorSendingCode,
    errorVerifyingCode,
    getNewPhone,
    requiresMfa,
    resendCode,
    sendCode,
    sendingCode,
    verifyCode,
  } = useMfa();

  const usesEmail = !!config.useEmailOverSMS && !config.mfaForceSMS;
  const contactDisplay = getMfaContactDisplay({
    user: userData?.user,
    newPhone: getNewPhone(),
    usesEmail,
  });

  const handleVerifySuccess = useCallback(() => {
    setUserVerified(true);
    if (afterVerifySuccess) {
      afterVerifySuccess();
    } else {
      widgetRef.current && widgetRef.current.launch();
    }
  }, [afterVerifySuccess, widgetRef]);

  const handleVerifyCode = useCallback(
    async (code: string) => {
      const success = await verifyCode(code);
      if (success) handleVerifySuccess();
    },
    [verifyCode, handleVerifySuccess]
  );

  const isLoading = isLinkingBankAccount || isLoadingWidget;

  useEffect(() => {
    if (!userVerified && !isLoadingWidget && !requiresMfa()) {
      handleVerifySuccess();
    }
  }, [userVerified, isLoadingWidget, requiresMfa, handleVerifySuccess]);

  return (
    <>
      {isLinkingBankAccount && <AeropayLoading />}

      <Box
        width={userVerified && !isLoading ? '342px' : 'auto'}
        height={userVerified && !isLoading ? '633px' : 'auto'}
      >
        {!userVerified && requiresMfa() && !isLinkingBankAccount && (
          <Mfa
            autoSendCode={canSendCode()}
            codeResent={codeResent}
            codeSent={codeSent}
            contactDisplay={contactDisplay}
            errorSendingCode={errorSendingCode}
            errorVerifyingCode={errorVerifyingCode}
            isLoading={isLoadingWidget}
            onCancel={onCancel}
            resendCode={resendCode}
            sendCode={sendCode}
            sendingCode={sendingCode}
            showCancelButton={showCancelButton}
            showResendButton={canResendCode()}
            usesEmail={usesEmail}
            verifyCode={handleVerifyCode}
          />
        )}

        <div id={WIDGET_CONTAINER_ID}></div>
      </Box>
    </>
  );
};
